import type { Product, AddOn } from './types';

/**
 * Catálogo de Cabina de Fotos.
 *
 * Los equipos se llaman igual que en el catálogo de la casa (Wally, Mirror
 * Black, Plataforma 360…) porque son los mismos equipos. Se omite la palabra
 * "Kuva" de los nombres que la llevaban, para no publicar una marca hermana
 * dentro de este sitio.
 *
 * El texto SÍ es propio y distinto: dos sitios con la misma redacción se
 * penalizan entre sí por contenido duplicado.
 *
 * Cada `id` es el slug público: /catalogo/:id
 */
export const PRODUCTS: Product[] = [
  // ── CABINAS ────────────────────────────────────────────────
  {
    id: 'wally',
    name: 'Wally',
    family: 'CABINAS',
    tags: ['Impresión', 'Madera'],
    shortDescription: 'Cabina de madera que se integra a la decoración.',
    description:
      'Estructura en madera vista, pantalla táctil e impresión inmediata. La pedimos cuando el salón ya tiene una decoración trabajada y el equipo no puede desentonar con ella.',
    longDescription:
      'Wally se comporta más como un mueble que como un equipo de alquiler: la madera está a la vista y los acabados aguantan que la cabina salga en las fotos del evento. Por dentro monta lo mismo que el resto del catálogo —pantalla táctil, luz continua e impresora térmica—, así que no se cambia calidad por estética. Es la primera opción en matrimonios al aire libre y en salones donde la decoración se contrató aparte.',
    seoTitle: 'Wally: Cabina de Fotos en Madera | Alquiler',
    seoDescription:
      'Alquiler de la cabina Wally: estructura en madera vista, pantalla táctil, impresión ilimitada y copia digital. Pensada para eventos con decoración cuidada.',
    keywords: 'cabina de fotos madera, cabina wally, fotocabina rustica, cabina de fotos boda campestre',
    idealFor: ['Bodas', 'Matrimonios al aire libre', 'Aniversarios', 'Cenas de empresa'],
    howItWorks: {
      step1: 'El invitado inicia la sesión desde la pantalla.',
      step2: 'La luz continua se ajusta y toma la secuencia.',
      step3: 'La tira sale impresa y la copia digital llega por QR.',
    },
    specs: { space: '2 m x 2 m', power: '110 v', capacity: 'Hasta 6 personas', setupTime: '40 minutos' },
    image: '/images/Wally.jpg',
    features: ['Impresión ilimitada', 'Madera vista', 'Props temáticos', 'Copia digital por QR'],
    faqs: [
      { q: '¿Cuántas fotos imprime?', a: 'Las que se tomen. La impresión es ilimitada durante las horas contratadas y cada grupo se lleva su tira.' },
      { q: '¿Se personaliza el diseño de la tira?', a: 'Sí. El marco se diseña con los nombres, la fecha o el motivo del evento y se aprueba antes de la fecha.' },
    ],
  },
  {
    id: 'ring',
    name: 'Ring',
    family: 'CABINAS',
    tags: ['Compacta', 'Digital'],
    shortDescription: 'Aro de luz sobre trípode: entra donde no cabe otra.',
    description:
      'Metro y medio de espacio y media hora de montaje. El aro de luz reparte la iluminación de forma pareja y entrega foto, GIF o boomerang directo al celular.',
    longDescription:
      'Es la opción para cuando el salón es pequeño, la fiesta es en casa o el espacio libre ya está comprometido. El aro rodea la lente, así que la luz llega de frente y sin sombras duras sobre el rostro. Trabaja sobre todo en digital: el invitado recibe el archivo al instante por QR, AirDrop, correo o mensaje, y vuelve a la pista sin esperar la impresión.',
    seoTitle: 'Cabina de Fotos con Aro de Luz | Alquiler Compacto',
    seoDescription:
      'Cabina compacta con aro de luz LED. Foto, GIF y boomerang con entrega digital inmediata por QR o AirDrop. Ideal para espacios pequeños y fiestas en casa.',
    keywords: 'cabina de fotos aro de luz, cabina de fotos pequeña, cabina de fotos gif boomerang, fotocabina compacta',
    idealFor: ['Cumpleaños', 'Fiestas en casa', 'Grados', 'Espacios reducidos'],
    howItWorks: {
      step1: 'El aro guía la sesión desde la pantalla.',
      step2: 'Se elige foto, GIF o boomerang.',
      step3: 'El archivo llega al celular por QR, AirDrop, correo o SMS.',
    },
    specs: { space: '1,5 m x 1,5 m', power: '110 v', capacity: 'Hasta 5 personas', setupTime: '25 minutos' },
    image: '/images/KuvaRing.jpg',
    features: ['Aro LED regulable', 'GIF y boomerang', 'Entrega digital inmediata', 'Montaje en 25 minutos'],
    faqs: [
      { q: '¿También imprime?', a: 'Su configuración base es digital, pero admite el módulo de impresión. Conviene indicarlo al cotizar.' },
    ],
  },
  {
    id: 'cabina-extreme',
    name: 'Cabina Extreme',
    family: 'CABINAS',
    tags: ['Cerrada', 'Clásica'],
    shortDescription: 'La fotocabina cerrada de siempre, con cortina.',
    description:
      'Cortina, banca y espacio propio. Sin público mirando, los invitados se sueltan y salen las fotos más espontáneas de la noche.',
    longDescription:
      'Es el formato original y sigue vigente por una razón concreta: al correr la cortina el invitado deja de posar para el salón. Lo que ocurre dentro no lo ve nadie más, y eso cambia por completo el tipo de foto que sale. Funciona especialmente bien con grupos que ya se conocen y en fiestas donde la gente entra varias veces durante la noche.',
    seoTitle: 'Cabina de Fotos Cerrada con Cortina | Fotocabina Clásica',
    seoDescription:
      'Alquiler de fotocabina cerrada con cortina. El formato clásico que da privacidad y produce las fotos más espontáneas del evento.',
    keywords: 'cabina de fotos cerrada, fotocabina con cortina, cabina de fotos clasica, fotomaton alquiler',
    idealFor: ['Fiestas', 'Grados', 'Cumpleaños', 'Eventos juveniles'],
    specs: { space: '2 m x 2 m', power: '110 v', capacity: 'Hasta 4 personas', setupTime: '45 minutos' },
    image: '/images/Extreme.jpg',
    features: ['Espacio cerrado', 'Impresión ilimitada', 'Props incluidos', 'Copia digital'],
  },
  {
    id: 'cabina-soft',
    name: 'Cabina Soft',
    family: 'CABINAS',
    tags: ['Luz suave', 'Retrato'],
    shortDescription: 'Sombrillas difusoras: el retrato más favorecedor.',
    description:
      'La luz pasa por sombrillas de estudio antes de llegar al rostro. El resultado se parece más a un retrato profesional que a una foto de fiesta.',
    longDescription:
      'Un flash directo marca sombras y brillos; una sombrilla difusora los reparte. Es la misma técnica de un estudio de retrato y la diferencia se nota en la cara de los invitados: sin ojeras marcadas ni reflejos. Se pide sobre todo en eventos de etiqueta, donde la gente se arregló y quiere una foto que pueda enmarcar.',
    seoTitle: 'Cabina de Fotos con Luz de Estudio | Retratos para Eventos',
    seoDescription:
      'Cabina de fotos con sombrillas difusoras de estudio. Luz suave y favorecedora para bodas, cenas de gala y eventos formales.',
    keywords: 'cabina de fotos luz de estudio, fotocabina profesional, cabina de fotos bodas elegantes',
    idealFor: ['Bodas', 'Cenas de gala', 'Eventos de empresa', 'Aniversarios'],
    specs: { space: '2,5 m x 2 m', power: '110 v', capacity: 'Hasta 6 personas', setupTime: '45 minutos' },
    image: '/images/Soft.jpg',
    features: ['Sombrillas difusoras', 'Calidad de retrato', 'Impresión ilimitada', 'Copia digital'],
  },

  // ── ESPEJOS ────────────────────────────────────────────────
  {
    id: 'mirror-black',
    name: 'Mirror Black',
    family: 'ESPEJOS',
    tags: ['Marco dorado', 'Etiqueta'],
    shortDescription: 'Marco barroco dorado para eventos de etiqueta.',
    description:
      'La misma tecnología táctil dentro de un marco dorado ornamentado, para montajes clásicos donde el equipo tiene que integrarse a la decoración.',
    longDescription:
      'En un evento de etiqueta, un equipo sin acabado junto a la entrada se ve como lo que es: un equipo. El marco dorado resuelve eso sin cambiar nada del funcionamiento —sigue siendo táctil, con firma sobre la foto e impresión en el momento—, pero se puede ubicar a la vista sin romper la línea del montaje.',
    seoTitle: 'Espejo Mágico Dorado Mirror Black | Alquiler Bodas',
    seoDescription:
      'Espejo fotográfico con marco dorado barroco para bodas y eventos formales. Pantalla táctil, firma digital e impresión en el momento.',
    keywords: 'espejo magico dorado, mirror black, espejo barroco eventos, espejo magico bodas elegantes',
    idealFor: ['Bodas', 'Eventos de gala', 'Quince años', 'Hoteles'],
    howItWorks: {
      step1: 'El invitado se ubica frente al espejo y elige el diseño en pantalla.',
      step2: 'Posa siguiendo las animaciones del reflejo.',
      step3: 'Firma la foto sobre la pantalla y la recibe impresa.',
    },
    specs: { space: '2 m x 2 m', power: '110 v', capacity: 'Sin límite por sesión', setupTime: '50 minutos' },
    image: '/images/MirrorBlack.jpg',
    features: ['Marco dorado barroco', 'Pantalla táctil', 'Firma sobre la foto', 'Alfombra opcional'],
    faqs: [
      { q: '¿Sirve para un evento de etiqueta?', a: 'Es justamente para eso. El marco dorado permite dejarlo a la vista dentro del montaje sin que se lea como equipo técnico.' },
    ],
  },
  {
    id: 'mirror-lux',
    name: 'Mirror Lux',
    family: 'ESPEJOS',
    tags: ['Táctil', 'Cuerpo completo'],
    shortDescription: 'Espejo táctil de cuerpo entero, alta rotación.',
    description:
      'El invitado se ve de pies a cabeza mientras posa, sigue las animaciones en el reflejo y firma su foto en la misma pantalla antes de imprimirla.',
    longDescription:
      'Su ventaja real no es el efecto sino el flujo. Al no haber cortina ni espacio cerrado, un grupo entra apenas sale el anterior, y en un evento con muchos invitados eso decide si la fila avanza o se estanca. Además el invitado se ve mientras posa, así que no hay fotos perdidas por no saber dónde estaba el encuadre.',
    seoTitle: 'Espejo Mágico Mirror Lux | Alquiler para Eventos',
    seoDescription:
      'Alquiler del espejo Mirror Lux: cuerpo completo, pantalla táctil, animaciones, firma digital e impresión inmediata. Pensado para eventos concurridos.',
    keywords: 'espejo magico, alquiler espejo magico, mirror lux, espejo fotografico eventos, espejo de fotos bodas',
    idealFor: ['Bodas', 'Quince años', 'Grados', 'Eventos con muchos invitados'],
    howItWorks: {
      step1: 'El invitado se para frente al espejo y sigue las animaciones.',
      step2: 'Posa de cuerpo completo viéndose en el reflejo.',
      step3: 'Firma la foto en la pantalla y la recibe impresa.',
    },
    specs: { space: '2 m x 2 m', power: '110 v', capacity: 'Sin límite por sesión', setupTime: '50 minutos' },
    image: '/images/MirrorLux.jpg',
    features: ['Reflejo de cuerpo completo', 'Pantalla táctil', 'Firma sobre la foto', 'Impresión ilimitada'],
    faqs: [
      { q: '¿Cuál conviene si tengo muchos invitados?', a: 'Cualquiera de los espejos. Al ser formatos abiertos y sin cortina, la rotación por hora es bastante más alta que en una cabina cerrada.' },
    ],
  },
  {
    id: 'mirror-glam',
    name: 'Mirror Glam',
    family: 'ESPEJOS',
    tags: ['Luces RGB', 'Portátil'],
    shortDescription: 'El espejo más reciente: luces RGB y chasis portátil.',
    description:
      'Recoge lo que funcionaba de los espejos anteriores y suma un borde de luces RGB configurable, una línea más limpia y un chasis fácil de mover.',
    longDescription:
      'Los espejos anteriores cumplían, pero pesaban y su acabado condicionaba la decoración. El Glam resuelve las dos cosas: el borde RGB se ajusta al color del montaje, así que encaja igual en una boda sobria que en una fiesta de quince, y al ser portátil es el único que se puede reubicar sin desmontar si el salón cambia de configuración durante la noche.',
    seoTitle: 'Mirror Glam: Espejo con Luces RGB | Alquiler',
    seoDescription:
      'Espejo de cuerpo completo con borde de luces RGB configurable, pantalla táctil, impresión inmediata y montaje portátil.',
    keywords: 'mirror glam, espejo magico luces rgb, espejo de fotos moderno, fotocabina espejo colombia',
    idealFor: ['Bodas', 'Quince años', 'Fiestas temáticas', 'Activaciones de marca'],
    specs: { space: '2 m x 2 m', power: '110 v', capacity: 'Sin límite por sesión', setupTime: '45 minutos' },
    image: '/images/MirrorGlam.jpg',
    features: ['Luces RGB configurables', 'Pantalla táctil', 'Impresión en el momento', 'Montaje portátil'],
  },

  // ── VIDEO ──────────────────────────────────────────────────
  {
    id: 'plataforma-360',
    name: 'Plataforma 360',
    family: 'VIDEO',
    tags: ['Video', 'Cámara lenta'],
    shortDescription: 'Plataforma giratoria con video en cámara lenta.',
    description:
      'El grupo se sube, el brazo con la cámara gira alrededor y el software devuelve el video editado, con música y cámara lenta, en menos de un minuto.',
    longDescription:
      'Es el formato que más contenido genera por invitado. Todo el ciclo —grabar, editar, entregar— cabe en menos de un minuto, así que el video se publica sin salir de la fiesta. A cambio exige lo que ninguna cabina exige: tres metros por tres libres, piso firme y nivelado, y altura despejada por encima de la cabeza, porque el brazo gira por arriba. Conviene confirmar el sitio antes de reservar.',
    seoTitle: 'Plataforma 360 | Alquiler de Cabina de Video Giratoria',
    seoDescription:
      'Alquiler de plataforma 360 con video en cámara lenta, edición automática y entrega inmediata. El formato más pedido en bodas y quince años.',
    keywords: 'plataforma 360, cabina 360, alquiler cabina 360, video 360 boda, cabina 360 precios',
    idealFor: ['Bodas', 'Quince años', 'Fiestas', 'Activaciones de marca'],
    howItWorks: {
      step1: 'El grupo se sube a la plataforma.',
      step2: 'El brazo gira grabando a alta velocidad.',
      step3: 'El video editado llega al celular en menos de un minuto.',
    },
    specs: { space: '3 m x 3 m (obligatorio)', power: '110 v', capacity: 'Hasta 4 personas', setupTime: '55 minutos' },
    image: '/images/Plataforma360.jpg',
    features: ['Video en cámara lenta', 'Edición automática', 'Música personalizada', 'Entrega digital'],
    faqs: [
      { q: '¿Cuánto espacio necesita exactamente?', a: 'Tres por tres metros libres y sin lámparas ni techos bajos encima: el brazo gira por encima del nivel de la cabeza.' },
      { q: '¿Se puede montar sobre pasto?', a: 'Necesita superficie firme y nivelada. Sobre pasto se instala con tarima o base, pero hay que preverlo antes del evento.' },
    ],
  },
  {
    id: 'glambot',
    name: 'Glambot',
    family: 'VIDEO',
    tags: ['Cine', 'Alta velocidad'],
    shortDescription: 'Brazo robótico con recorridos de cámara programados.',
    description:
      'Un brazo motorizado repite exactamente el mismo movimiento en cada toma, algo imposible a pulso. Graba a alta velocidad y se entrega en cámara lenta.',
    longDescription:
      'Es el equipo detrás de esas tomas de premiación donde la cámara vuela alrededor de la persona. Como el recorrido está programado, todas las tomas salen idénticas y con acabado cinematográfico, lo que permite montar después una pieza con varios invitados sin que se note el corte. Requiere operador dedicado y área despejada, así que se cotiza aparte del resto del catálogo.',
    seoTitle: 'Glambot: Brazo Robótico de Video | Alquiler para Eventos',
    seoDescription:
      'Alquiler de brazo robótico Glambot: recorridos de cámara programados y grabación en cámara lenta con acabado de cine, para eventos de alto perfil.',
    keywords: 'glambot, brazo robotico video eventos, camara lenta eventos, video alfombra roja',
    idealFor: ['Lanzamientos', 'Premiaciones', 'Eventos de marca', 'Contenido para redes'],
    specs: { space: '4 m x 3 m', power: '110 v', capacity: '1 a 3 personas', setupTime: '60 minutos' },
    image: '/images/Glambot.jpg',
    features: ['Movimiento programado', 'Alta velocidad', 'Operador dedicado', 'Entrega digital'],
  },
  {
    id: '360-aereo',
    name: '360 Aéreo',
    family: 'VIDEO',
    tags: ['Sin plataforma', 'Grupos'],
    shortDescription: 'Video 360 suspendido, hasta 10 personas.',
    description:
      'La misma idea de la Plataforma 360 pero colgada desde arriba. Sin tarima en el piso entran grupos grandes y pueden moverse o bailar con libertad.',
    longDescription:
      'La plataforma es lo que limita al 360 clásico: obliga a subirse y restringe cuánta gente cabe. Al suspender el sistema desde una estructura superior el piso queda libre y admite hasta diez personas a la vez. Es la versión que recomendamos para activaciones con mucho flujo de público y para fiestas donde detenerse a subir una tarima rompe el ritmo.',
    seoTitle: '360 Aéreo | Video 360 Suspendido para Grupos Grandes',
    seoDescription:
      'Alquiler de 360 aéreo suspendido, sin plataforma en el piso. Admite hasta 10 personas y permite movimiento libre en fiestas y activaciones.',
    keywords: '360 aereo, video 360 sin plataforma, 360 colgante eventos, cabina 360 grupos grandes',
    idealFor: ['Activaciones', 'Fiestas grandes', 'Ferias', 'Grupos numerosos'],
    specs: { space: '4 m x 4 m', power: '110 v', capacity: 'Hasta 10 personas', setupTime: '75 minutos' },
    image: '/images/innovators/360_aereo.jpg',
    features: ['Sin plataforma', 'Hasta 10 personas', 'Estructura suspendida', 'Edición automática'],
  },
  {
    id: '180-aereo',
    name: '180 Aéreo',
    family: 'VIDEO',
    tags: ['Arco', 'Photocall'],
    shortDescription: 'Arco de cámaras que congela el movimiento.',
    description:
      'Un arco con varias cámaras dispara al mismo tiempo y genera un barrido de 180 grados: la escena queda congelada mientras el punto de vista se desplaza.',
    longDescription:
      'El efecto es el de "bullet time": el invitado salta o lanza algo al aire y el resultado detiene ese instante mientras la cámara lo rodea. Al ser un arco abierto y sin nada que subir, funciona muy bien como photocall en entradas y zonas de paso, donde la gente pasa sin haber planeado detenerse.',
    seoTitle: '180 Aéreo | Arco de Cámaras Bullet Time para Eventos',
    seoDescription:
      'Alquiler de arco 180 que congela el movimiento con un barrido de cámaras. Ideal como photocall en entradas de eventos y activaciones.',
    keywords: '180 aereo, arco 180 fotos, bullet time evento, photocall 180 grados',
    idealFor: ['Photocall de entrada', 'Activaciones', 'Ferias', 'Fiestas'],
    specs: { space: '4 m x 2,5 m', power: '110 v', capacity: '1 a 4 personas', setupTime: '70 minutos' },
    image: '/images/innovators/180_aereo.jpg',
    features: ['Efecto bullet time', 'Formato abierto', 'Alta rotación', 'Entrega digital'],
  },

  // ── EXPERIENCIAS Y SPOTS ───────────────────────────────────
  {
    id: 'vogue-booth',
    name: 'Vogue Booth',
    family: 'ESCENARIOS',
    tags: ['LED', 'Espejos'],
    shortDescription: 'Túnel de espejos y LED con profundidad infinita.',
    description:
      'Un pasillo forrado en espejos y luces LED programables. La repetición de los reflejos crea la sensación de un espacio sin fondo y el video parece un videoclip.',
    longDescription:
      'Más que un equipo es un espacio construido: el invitado entra y camina dentro del túnel mientras las luces reaccionan. Su valor añadido es que llama la atención por sí solo dentro del salón, incluso en los ratos en que nadie lo está usando, así que cumple también como pieza de montaje. Necesita bastante más tiempo de instalación que el resto del catálogo.',
    seoTitle: 'Vogue Booth: Túnel de Luces LED | Alquiler para Eventos',
    seoDescription:
      'Alquiler de túnel de espejos con luces LED programables para grabar videos con estética de videoclip. Pieza central para fiestas y activaciones.',
    keywords: 'vogue booth, tunel de luces led, tunel de espejos eventos, cabina infinity',
    idealFor: ['Fiestas temáticas', 'Activaciones', 'Lanzamientos', 'Quince años'],
    specs: { space: '5 m x 2,5 m', power: '110 v', capacity: '1 a 4 personas', setupTime: '90 minutos' },
    image: '/images/innovators/vogue_booth.jpg',
    features: ['Túnel de espejos', 'LED programable', 'Video tipo videoclip', 'Entrega digital'],
  },
  {
    id: 'cabina-telefonica',
    name: 'Audio Guestbook',
    family: 'ESCENARIOS',
    tags: ['Audio', 'Recuerdo'],
    shortDescription: 'Teléfono antiguo que graba mensajes de voz.',
    description:
      'El invitado descuelga, escucha una indicación grabada por los anfitriones y deja su mensaje. Al final del evento se entregan todos los audios organizados.',
    longDescription:
      'Reemplaza el libro de firmas por algo que sí se vuelve a abrir. La carcasa es la del teléfono original, pero por dentro lleva un sistema de grabación: al colgar, el mensaje queda guardado. Es el único servicio del catálogo que no produce imagen y, precisamente por eso, suele ser el que más impresiona cuando se escucha semanas después.',
    seoTitle: 'Audio Guestbook | Teléfono de Mensajes de Voz para Eventos',
    seoDescription:
      'Alquiler de teléfono vintage que graba mensajes de voz de los invitados. Alternativa al libro de firmas, con los audios entregados al final del evento.',
    keywords: 'audio guestbook, telefono libro de firmas, mensajes de voz boda, cabina telefonica eventos',
    idealFor: ['Bodas', 'Aniversarios', 'Despedidas', 'Homenajes'],
    howItWorks: {
      step1: 'El invitado descuelga el teléfono.',
      step2: 'Escucha el saludo que grabaron los anfitriones.',
      step3: 'Deja su mensaje y cuelga: queda guardado.',
    },
    specs: { space: '1 m x 1 m', power: 'Batería interna', capacity: 'Un invitado a la vez', setupTime: '15 minutos' },
    image: '/images/innovators/cabina_telefonica.jpg',
    features: ['Teléfono vintage', 'Saludo personalizado', 'Audios entregados al final', 'No requiere operador'],
    faqs: [
      { q: '¿Cómo se entregan los audios?', a: 'Al terminar el evento se descargan los mensajes y se entregan en archivos digitales, en el orden en que se grabaron.' },
    ],
  },
  {
    id: 'ai-avatar',
    name: 'AI Avatar Studio',
    family: 'ESCENARIOS',
    tags: ['IA', 'Retrato'],
    shortDescription: 'Retratos generados con inteligencia artificial.',
    description:
      'Toma una foto real del invitado y genera en sitio un retrato con el estilo definido para el evento, manteniendo el rostro reconocible.',
    longDescription:
      'Antes del evento se define el estilo —una época, una estética o el universo visual de una marca— y durante la fiesta cada invitado recibe su versión. El punto de equilibrio está en que la cara siga siendo reconocible: si no se reconoce, deja de ser un recuerdo y pasa a ser una ilustración. La generación toma alrededor de un minuto por persona, así que en eventos muy concurridos conviene montar más de una estación.',
    seoTitle: 'AI Avatar Studio | Retratos con Inteligencia Artificial',
    seoDescription:
      'Experiencia de retratos generados con inteligencia artificial en el evento. Estilo definido a medida, entrega digital y opción de impresión.',
    keywords: 'cabina de fotos inteligencia artificial, retratos ia eventos, ai photobooth colombia, experiencia con ia evento',
    idealFor: ['Activaciones de marca', 'Eventos de tecnología', 'Fiestas temáticas', 'Ferias'],
    specs: { space: '2 m x 2 m', power: '110 v', capacity: 'Un invitado a la vez', setupTime: '40 minutos' },
    /* Imagen propia de este sitio. La anterior era la misma que usan
       Enyoik y Kuva —y llevaba la marca de un tercero—; los tres sitios
       del cliente no deben compartir foto de producto. */
    image: '/images/innovators/ai_avatar.jpg',
    features: ['Estilo definido a medida', 'Generación en sitio', 'Entrega digital', 'Impresión opcional'],
    faqs: [
      { q: '¿Cuánto tarda cada retrato?', a: 'Alrededor de un minuto por invitado. En eventos muy concurridos se montan dos estaciones para que la fila no se detenga.' },
    ],
  },
  {
    id: 'cabina-espejada',
    name: 'Cabina Espejada',
    family: 'ESCENARIOS',
    tags: ['Spot', 'Decoración'],
    shortDescription: 'Cubo de espejos y globos en el color de tu evento.',
    description:
      'Un cubo de espejos con globos metalizados del color que elijas. Los reflejos multiplican la luz del salón y la pieza decora incluso cuando nadie la usa.',
    longDescription:
      'Es un spot fotográfico, no una cabina con operador: los invitados entran, se fotografían con su propio celular y salen. Como los espejos y los globos se eligen en el color del evento, la pieza cumple doble función —punto de fotos y elemento de decoración— y no queda apagada en los ratos muertos. Si además quieres las fotos impresas, admite sumarle una fotocabina desde dos horas, con salida en postal de 10x15 cm o en formato polaroid.',
    seoTitle: 'Cabina Espejada | Spot Fotográfico de Espejos y Globos',
    seoDescription:
      'Spot fotográfico de espejos y globos metalizados en el color de tu evento. Funciona como decoración y admite fotocabina con impresión desde dos horas.',
    keywords: 'cabina espejada, spot de fotos eventos, photo opportunity evento, decoracion fotografica fiesta',
    idealFor: ['Quince años', 'Cumpleaños', 'Fiestas temáticas', 'Activaciones de marca'],
    image: '/images/CabinaEspejada.jpg',
    features: [
      'Globos en el color del evento',
      'Funciona como decoración',
      'Fotocabina opcional desde 2 horas',
      'Impresión en postal 10x15 o polaroid',
    ],
  },
  {
    id: 'magazine',
    name: 'Magazine',
    family: 'ESCENARIOS',
    tags: ['Spot', 'Personalizado'],
    shortDescription: 'Marco de portada de revista, diseñado a medida.',
    description:
      'Un marco de gran formato con estética de portada de revista, personalizado con el nombre, la fecha y los titulares que quieras. El invitado se asoma y queda de portada.',
    longDescription:
      'La portada se diseña antes del evento con la información que definas, así que ningún montaje se repite. Como los demás spots funciona sin operador y hace las veces de decoración durante toda la fiesta. Se le puede sumar una fotocabina desde dos horas para que las fotos, además de quedar en el celular, salgan impresas en postal de 10x15 cm.',
    seoTitle: 'Magazine | Marco de Portada de Revista para Eventos',
    seoDescription:
      'Spot fotográfico con marco de portada de revista personalizado con tu nombre, fecha y titulares. Admite fotocabina con impresión desde dos horas.',
    keywords: 'marco portada de revista, spot fotografico quince años, photo booth revista, decoracion fotografica evento',
    idealFor: ['Quince años', 'Cumpleaños', 'Despedidas', 'Eventos de marca'],
    image: '/images/Magazine.jpg',
    features: [
      'Portada diseñada a medida',
      'Funciona como decoración',
      'Fotocabina opcional desde 2 horas',
      'Impresión en postal 10x15',
    ],
  },
];

/** Complementos que se suman a cualquier cabina. */
export const ADD_ONS: AddOn[] = [
  { id: 'photobooth', name: 'Tiras de fotos', description: 'El formato clásico de tirilla impresa, personalizada con el motivo del evento.', image: '/images/photoboothtiras.jpg' },
  { id: 'chroma', name: 'Chroma key', description: 'Fondo verde para reemplazar el escenario por cualquier imagen o video.', image: '/images/chromakey.jpg' },
  { id: 'videobooth', name: 'Videobooth', description: 'Los invitados graban un mensaje corto en video para los anfitriones.', image: '/images/videobooth.jpg' },
  { id: 'boomerang', name: 'Boomerang', description: 'Clip corto en bucle, el formato que mejor rinde en historias.', image: '/images/boomerang.jpg' },
  { id: 'encuestas', name: 'Encuestas', description: 'Preguntas en pantalla durante la sesión para recoger datos del público.', image: '/images/encuestas.jpg' },
  { id: 'hashtag', name: 'Hashtag printing', description: 'Imprime las fotos que los invitados publican con el hashtag del evento.', image: '/images/hashtagprinting.jpg' },
  { id: 'slowmo', name: 'Slow motion', description: 'Grabación a alta velocidad para reproducir el momento en cámara lenta.', image: '/images/slowmotion.jpg' },
  { id: 'adicionales', name: 'Props y álbum', description: 'Accesorios temáticos y álbum físico para pegar las tiras durante el evento.', image: '/images/adicionales.jpg' },
];

export const getProduct = (id?: string) => PRODUCTS.find((p) => p.id === id);

export const FAMILIES = [
  {
    id: 'CABINAS' as const,
    title: 'Cabinas de fotos',
    intro:
      'El formato principal: foto impresa en el momento, marco personalizado y copia digital. Es lo que la mayoría busca cuando dice "cabina de fotos".',
  },
  {
    id: 'ESPEJOS' as const,
    title: 'Espejos',
    intro:
      'Formato abierto y de cuerpo completo. Mueven más gente por hora que una cabina cerrada, así que son la opción cuando hay muchos invitados.',
  },
  {
    id: 'VIDEO' as const,
    title: 'Video',
    intro:
      'Cuando lo que se busca es contenido para redes: giros de 360°, cámara lenta y tomas de brazo robótico, editadas y listas para compartir.',
  },
  {
    id: 'ESCENARIOS' as const,
    title: 'Experiencias y spots',
    intro:
      'Montajes que funcionan como atracción del evento por sí mismos, más allá de tomar la foto. Varios no necesitan operador.',
  },
];
